import { useState } from "react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-python";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import styles from "./EditableFunctionBlock.module.css";

// Left padding carves out the gutter the injected line numbers sit in (see
// `.lineNumber` in the CSS module: width 40px, so code starts 8px past it).
const GUTTER_WIDTH = 48;
const EDITOR_PADDING = { top: 10, right: 12, bottom: 10, left: GUTTER_WIDTH };

// Prism highlighting with a per-line gutter number. The number spans are
// absolutely positioned into the padding gutter by CSS, so they don't offset the
// code text from the transparent textarea overlaying it.
const highlightWithLineNumbers = (code: string): string => {
  // Loaded by the prism-python import above; missing means a broken build, so
  // fail loudly rather than silently rendering unhighlighted code.
  const grammar = Prism.languages.python;
  if (!grammar) {
    throw new Error("prismjs python grammar is not loaded");
  }
  return Prism.highlight(code, grammar, "python")
    .split("\n")
    .map(
      (line, i) => `<span class="${styles.lineNumber}">${i + 1}</span>${line}`
    )
    .join("\n");
};

interface EditableFunctionBlockProps {
  readablePerilName: string;
  description: string;
  /** Card-specific extra header content (e.g. the limit's document-quotation tooltip). */
  headerExtra?: React.ReactNode;
  /** Card-specific Test button; the Edit control renders immediately after it. */
  testButton: React.ReactNode;
  /** The function to display and edit: the override when set, else the generated one. */
  functionStr: string;
  /** Whether a user override is currently in effect (drives the badge and revert action). */
  hasOverride: boolean;
  isLightMode: boolean;
  isSaving: boolean;
  /** Persist the edited text as the override. Rejects on failure so the editor stays open. */
  onSave: (nextOverride: string) => Promise<void>;
  /** Clear the override so the generated function is used again. */
  onRevert: () => Promise<void>;
}

export const EditableFunctionBlock: React.FC<EditableFunctionBlockProps> = ({
  readablePerilName,
  description,
  headerExtra,
  testButton,
  functionStr,
  hasOverride,
  isLightMode,
  isSaving,
  onSave,
  onRevert,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(functionStr);

  const startEditing = () => {
    setDraft(functionStr);
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      // Save failed; the caller surfaces the error and we keep the editor open.
    }
  };

  return (
    <div className={styles.verticalContainerSmall}>
      <div className={styles.horizontalContainer}>
        <span className={styles.header}>{readablePerilName}</span>
        {hasOverride && !editing && <Tag value="Edited" severity="info" />}
        {headerExtra}
        {testButton}
        {editing ? (
          <>
            <Button
              label="Save"
              icon="pi pi-check"
              size="small"
              style={{ padding: "4px 6px" }}
              loading={isSaving}
              disabled={draft === functionStr || draft.trim() === ""}
              onClick={handleSave}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              text
              size="small"
              style={{ padding: "4px 6px" }}
              disabled={isSaving}
              onClick={() => setEditing(false)}
            />
          </>
        ) : (
          <>
            <Button
              label="Edit"
              icon="pi pi-pencil"
              outlined
              size="small"
              style={{ padding: "4px 6px" }}
              onClick={startEditing}
            />
            {hasOverride && (
              <Button
                label="Revert to generated"
                icon="pi pi-undo"
                text
                size="small"
                severity="secondary"
                style={{ padding: "4px 6px" }}
                loading={isSaving}
                onClick={() => onRevert()}
              />
            )}
          </>
        )}
      </div>
      <span style={{ fontSize: "14px" }}>{description}</span>
      {/* One editor for both modes: read-only until the user clicks Edit. Same
          component in both states means no box-model drift / layout shift. */}
      <Editor
        value={editing ? draft : functionStr}
        onValueChange={setDraft}
        highlight={highlightWithLineNumbers}
        readOnly={!editing}
        tabSize={4}
        insertSpaces
        padding={EDITOR_PADDING}
        className={`${styles.codeEditor} ${
          isLightMode ? styles.light : styles.dark
        }`}
      />
    </div>
  );
};
